/**
 * Interoperability Feature — View-model / mock-domain types
 *
 * These types model the Interoperability integration surface (users, HL7 field &
 * value mapping, transaction monitoring). Interoperability
 * does not yet have a backend API, so these types are kept local to this
 * module (mock-data driven) rather than in `@tms-phoenix/tms-types`. Once a
 * real Interoperability API contract exists, promote the domain models (not the
 * UI-only shapes) to Zod schemas in `@tms-phoenix/tms-types`.
 */

// ── Interoperability User Management ─────────────────────────────────────────────

export const INTEROPERABILITY_ROLE_VALUES = ['ADMIN', 'DEVELOPER', 'OPERATOR', 'MONITOR'] as const;
export type InteroperabilityRole = (typeof INTEROPERABILITY_ROLE_VALUES)[number];

export const INTEROPERABILITY_USER_STATUS_VALUES = ['ACTIVE', 'INACTIVE', 'LOCKED'] as const;
export type InteroperabilityUserStatus = (typeof INTEROPERABILITY_USER_STATUS_VALUES)[number];

export interface InteroperabilityUser {
  id: string;
  username: string;
  role: InteroperabilityRole;
  status: InteroperabilityUserStatus;
  lastLogin: string | null;
  createdAt: string;
}

export interface InteroperabilityUserFormValues {
  username: string;
  password: string;
  confirmPassword: string;
  role: InteroperabilityRole | null;
  status: InteroperabilityUserStatus | null;
}

export interface ResetPasswordFormValues {
  newPassword: string;
  confirmNewPassword: string;
}

// ── Field Mapping ───────────────────────────────────────────────────────────

export const MAPPING_RULE_TYPE_VALUES = [
  'DIRECT_STANDARD',
  'DIRECT_OVERRIDE',
  'FORMAT_TRANSFORM',
  'CONSTANT',
  'CROSSWALK',
] as const;
export type MappingRuleType = (typeof MAPPING_RULE_TYPE_VALUES)[number];

export const MAPPING_CATEGORY_VALUES = ['STANDARD', 'CUSTOMIZED', 'CUSTOM_SEGMENT'] as const;
export type MappingCategory = (typeof MAPPING_CATEGORY_VALUES)[number];

export interface FieldMapping {
  id: string;
  saasField: string;
  hl7Path: string;
  ruleType: MappingRuleType;
  category: MappingCategory;
  /** Standard/default rows are read-only and cannot be edited. */
  isStandard: boolean;
  /** Whether this field has an associated coded value map (crosswalk). */
  hasValueMap: boolean;
  /** Field key used to look up this field's value-map rows. */
  valueMapFieldKey?: string;
  constantValue?: string;
  fallback: string;
}

export interface FieldMappingFormValues {
  saasField: string;
  hl7Path: string;
  ruleType: MappingRuleType | null;
  constantValue: string;
  fallback: string;
}

// ── Value Mapping ───────────────────────────────────────────────────────────

export const VALUE_MATCH_TYPE_VALUES = ['EXACT', 'CATCH_ALL'] as const;
export type ValueMatchType = (typeof VALUE_MATCH_TYPE_VALUES)[number];

export interface ValueMappingFieldOption {
  fieldKey: string;
  saasField: string;
  hl7Path: string;
}

export interface ValueMappingRow {
  id: string;
  fieldKey: string;
  sourceValue: string;
  matchType: ValueMatchType;
  canonicalValue: string;
  isFallback: boolean;
}

export interface ValueMappingFormValues {
  sourceValue: string;
  matchType: ValueMatchType;
  canonicalValue: string;
  isFallback: boolean;
}

// ── Transaction Monitoring ──────────────────────────────────────────────────

export const TRANSACTION_STATUS_VALUES = ['SUCCESS', 'FAILED', 'RETRYING', 'QUEUED'] as const;
export type TransactionStatus = (typeof TRANSACTION_STATUS_VALUES)[number];

export const PIPELINE_STAGE_KEY_VALUES = ['RECEIVED', 'PARSED', 'MAPPED', 'API'] as const;
export type PipelineStageKey = (typeof PIPELINE_STAGE_KEY_VALUES)[number];

export const PIPELINE_STAGE_STATE_VALUES = ['DONE', 'FAILED', 'ACTIVE', 'PENDING'] as const;
export type PipelineStageState = (typeof PIPELINE_STAGE_STATE_VALUES)[number];

export interface PipelineStage {
  key: PipelineStageKey;
  state: PipelineStageState;
}

export interface TransactionRetryEntry {
  attempt: number;
  timestamp: string;
  result: string;
}

export interface TransactionRecord {
  id: string;
  receivedAt: string;
  messageControlId: string;
  client: string;
  messageType: string;
  pipeline: PipelineStage[];
  status: TransactionStatus;
  apiResponse: string;
  retryCount: number;
  maxRetries: number;
  errorDetail: string | null;
  /** Sanitized mock HL7 payload — never real PHI. */
  rawMessage: string;
  /** Sanitized mock parsed canonical JSON. */
  parsedCanonicalJson: string;
  apiRequestSummary: string;
  apiResponseSummary: string;
  retryHistory: TransactionRetryEntry[];
}

export interface TransactionFilters {
  client: string | null;
  status: TransactionStatus | null;
  messageType: string | null;
  search: string;
}

export interface EditReplayFormValues {
  payload: string;
}

// ── Interoperability Configuration (Settings) ─────────────────────────────────────

export const INTEROPERABILITY_CONNECTION_STATE_VALUES = ['CONNECTED', 'DISCONNECTED'] as const;
export type InteroperabilityConnectionState = (typeof INTEROPERABILITY_CONNECTION_STATE_VALUES)[number];

export interface InteroperabilityConnectionStatus {
  state: InteroperabilityConnectionState;
  lastCheckedAt: string;
}

export const INTEROPERABILITY_CERTIFICATE_STATUS_VALUES = ['VALID', 'EXPIRED', 'REVOKED'] as const;
export type InteroperabilityCertificateStatus = (typeof INTEROPERABILITY_CERTIFICATE_STATUS_VALUES)[number];

export interface InteroperabilityCertificate {
  fileName: string;
  status: InteroperabilityCertificateStatus;
  expiryDate: string;
}
